import React from 'react';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

const ImportLog = ({ logs, importStats, importSuccess, onComplete }) => {
  const { user } = useAuth();

  // Group logs by their phase
  const groupedLogs = logs.reduce((acc, log) => {
    const phase = log.phase || 'general';
    if (!acc[phase]) {
      acc[phase] = [];
    }
    acc[phase].push(log);
    return acc;
  }, {});

  // Order phases logically
  const phaseOrder = [
    'file-processing',
    'validation',
    'pcgs-updates',
    'import',
    'transactions',
    'general'
  ];

  const getPhaseLabel = (phase) => {
    const labels = {
      'file-processing': 'File Processing',
      'validation': 'Data Validation',
      'pcgs-updates': 'PCGS Updates',
      'import': 'Import Process',
      'transactions': 'Transaction Processing',
      'general': 'General'
    };
    return labels[phase] || phase;
  };

  return (
    <div className="space-y-6">
      {/* Import Logs */}
      {logs.length > 0 && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Import Log</h3>
            
            <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-auto">
              {phaseOrder.map(phase => {
                const phaseLogs = groupedLogs[phase];
                if (!phaseLogs?.length) return null;

                return (
                  <div key={phase} className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{getPhaseLabel(phase)}</h4>
                    {phaseLogs.map((log, index) => (
                      <div 
                        key={`${phase}-${index}`}
                        className={`text-sm mb-1 pl-4 ${
                          log.type === 'error' ? 'text-red-600' : 
                          log.type === 'warn' ? 'text-yellow-600' : 
                          log.type === 'success' ? 'text-green-600' :
                          'text-gray-600'
                        }`}
                      >
                        <span className="text-gray-400">
                          [{formatDate(log.timestamp, { user, includeTime: true })}]
                        </span>{' '}
                        {log.message}
                        {log.details && (
                          <div className="ml-6 mt-1 text-xs text-gray-500">
                            {log.details}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {importSuccess && importStats && (
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="sm:flex sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Import Completed Successfully
                </h3>
                <div className="mt-2 max-w-xl text-sm text-gray-500">
                  <p>Successfully imported {importStats.success} out of {importStats.total} coins.</p>
                  {importStats.soldCoins > 0 && (
                    <p className="mt-1">
                      Including {importStats.soldCoins} sold coin(s) with transactions.
                    </p>
                  )}
                  {importStats.errors > 0 && (
                    <p className="mt-1 text-yellow-600">
                      {importStats.errors} coin(s) failed to import. Please check the logs for details.
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-5 sm:mt-0 sm:ml-6 sm:flex sm:flex-shrink-0 sm:items-center">
                <button
                  type="button"
                  onClick={onComplete}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Go to Inventory
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportLog;
